import { getPath, setPath, type DraftData } from '../lib/object-path';

/** Mesma chave do HTML original: rascunhos já existentes continuam valendo. */
const STORAGE_KEY = 'poa-ia156-fontes-v1';
const SAVE_DEBOUNCE_MS = 300;

/** Caminho que o HTML original usava para guardar só os nomes dos anexos. */
const LEGACY_ATTACHMENTS_PATH = 'fontes.arquivos';

interface StoredDraft {
  data: DraftData;
  cur: number;
  at: number;
}

/**
 * Estado do formulário + rascunho automático no localStorage.
 * Arquivos não cabem no localStorage; ficam no {@link AttachmentBag}.
 */
export class DraftStore {
  data: DraftData = {};
  step = 0;
  /** O rascunho recuperado citava anexos que precisam ser selecionados de novo. */
  hadStaleAttachments = false;

  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly storage: Storage = window.localStorage,
    private readonly onSaved: (at: Date) => void = () => {},
  ) {}

  load(): this {
    try {
      const saved = JSON.parse(this.storage.getItem(STORAGE_KEY) ?? 'null') as StoredDraft | null;
      if (saved?.data) {
        this.data = saved.data;
        this.step = saved.cur || 0;
      }
    } catch {
      /* rascunho corrompido: começa do zero */
    }
    const legacy = getPath(this.data, LEGACY_ATTACHMENTS_PATH);
    if (Array.isArray(legacy) && legacy.length) this.hadStaleAttachments = true;
    if (legacy !== undefined) setPath(this.data, LEGACY_ATTACHMENTS_PATH, undefined);
    return this;
  }

  get(path: string): unknown {
    return getPath(this.data, path);
  }

  set(path: string, value: unknown): void {
    setPath(this.data, path, value);
    this.persist();
  }

  goTo(step: number): void {
    this.step = step;
    this.persist();
  }

  persist(): void {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.flush(), SAVE_DEBOUNCE_MS);
  }

  flush(): void {
    clearTimeout(this.timer);
    const at = new Date();
    try {
      const draft: StoredDraft = { data: this.data, cur: this.step, at: at.getTime() };
      this.storage.setItem(STORAGE_KEY, JSON.stringify(draft));
      this.onSaved(at);
    } catch {
      /* storage cheio ou bloqueado: segue sem rascunho */
    }
  }

  clear(): void {
    clearTimeout(this.timer);
    this.data = {};
    this.step = 0;
    try {
      this.storage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
}
